/**
 * Native browser notifications for new messages. This is a standalone
 * module deliberately kept separate from real Push API support (service
 * worker + VAPID keys + a subscriptions table) so that follow-up work is
 * additive rather than a rewrite — this pass only covers notifying the
 * user while they have a tab open (foreground/backgrounded), not while
 * the site is fully closed.
 */

const STORAGE_KEY = 'messageNotificationsEnabled';

function isEnabled() {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (e) {
        return false;
    }
}

function setEnabled(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
    } catch (e) {
        // Private browsing / blocked storage — the preference just won't persist.
    }

    window.dispatchEvent(new CustomEvent('message-notifications-changed', { detail: { enabled: value } }));
}

async function requestPermission() {
    if (!('Notification' in window)) {
        return false;
    }

    if (Notification.permission === 'granted') {
        return true;
    }

    if (Notification.permission === 'denied') {
        return false;
    }

    return (await Notification.requestPermission()) === 'granted';
}

function notify(conversationId) {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
        return;
    }

    const notification = new Notification('New message on AirsoftHub', {
        body: 'You have a new message in your inbox.',
        icon: '/img/favicon/favicon-96x96.png',
        tag: 'airsoft-hub-message',
    });

    notification.onclick = () => {
        window.focus();

        if (conversationId) {
            window.location.href = `/message-centre?chat=${conversationId}`;
        }

        notification.close();
    };
}

let lastUnreadCount = null;

document.addEventListener('livewire:init', () => {
    Livewire.on('unread-count', (data) => {
        const count = data?.count ?? 0;

        // Only notify once we have a previous count to compare against
        // (avoids notifying on the very first load), the count actually
        // went up, notifications are turned on, and the tab isn't the
        // one currently focused on the conversation.
        if (isEnabled() && lastUnreadCount !== null && count > lastUnreadCount && document.hidden) {
            notify();
        }

        lastUnreadCount = count;
    });
});

window.messageNotifications = {
    isEnabled,
    async enable() {
        const granted = await requestPermission();
        setEnabled(granted);

        return granted;
    },
    disable() {
        setEnabled(false);
    },
};
